import React from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { UploadCloud, FileCheck2, Trash2, Download, Eye, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { fmtDate } from '../lib/format';
import api, { API } from '../lib/api';

export const SignedReportUpload = ({ session, canEdit }) => {
  const { uploadSignedReport, deleteSignedReport } = useApp();
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const signed = session.signed_report;

  const handleFiles = async (files) => {
    if (!files || !files[0]) return;
    const f = files[0];
    if (f.size > 5 * 1024 * 1024) { toast({ title: 'File too large', description: 'Please upload under 5 MB.' }); return; }
    if (!/(pdf|png|jpe?g)$/i.test(f.name)) { toast({ title: 'Unsupported format', description: 'Upload PDF, PNG or JPG.' }); return; }
    setUploading(true); setProgress(0);
    try {
      await uploadSignedReport(session.id, f, (p) => {
        if (p.total) setProgress(Math.round((p.loaded / p.total) * 100));
      });
      toast({ title: 'Signed report uploaded', description: `${f.name} attached to the session.` });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.apiMessage || 'Please try again' });
    } finally { setUploading(false); setProgress(0); }
  };

  const remove = async () => {
    try { await deleteSignedReport(session.id); toast({ title: 'Signed report removed' }); }
    catch (e) { toast({ title: 'Delete failed', description: e?.apiMessage || 'Please try again' }); }
  };

  const preview = async () => {
    try {
      const r = await fetch(api.signedReportUrl(session.id), { credentials: 'include' });
      if (!r.ok) throw new Error('Failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) toast({ title: 'Popup blocked', description: 'Please allow popups to preview.' });
      // don't revoke immediately — leave it for the tab to use
    } catch (e) {
      toast({ title: 'Preview failed', description: e?.apiMessage || 'Please try again' });
    }
  };

  const download = () => {
    // Force download via anchor. Cookies are sent because we're same-origin (via ingress /api).
    const a = document.createElement('a');
    a.href = api.signedReportUrl(session.id);
    a.rel = 'noopener';
    a.click();
  };

  const fmtSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${(b / 1024).toFixed(0)} KB`;

  return (
    <Card className="border-brand-border">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <FileCheck2 className="h-4 w-4 text-brand-green" />
          <div className="text-sm font-semibold text-brand-indigo">Signed Report</div>
        </div>

        {signed ? (
          <div className="border border-brand-border rounded-md p-4 bg-brand-bg/50">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-brand-green/15 flex items-center justify-center">
                <FileCheck2 className="h-5 w-5 text-brand-greenDark" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-indigo truncate">{signed.name}</div>
                <div className="text-[11px] text-muted-foreground">Uploaded by {signed.uploaded_by} · {fmtDate(signed.uploaded_at)} · {fmtSize(signed.size || 0)}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={preview} className="border-brand-border"><Eye className="h-3.5 w-3.5 mr-1.5" /> Preview</Button>
              <Button variant="outline" size="sm" onClick={download} className="border-brand-border"><Download className="h-3.5 w-3.5 mr-1.5" /> Download</Button>
              {canEdit && (
                <Button variant="ghost" size="sm" onClick={remove} className="text-brand-red hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove</Button>
              )}
            </div>
          </div>
        ) : (
          canEdit ? (
            <div
              onClick={() => !uploading && inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (!uploading) handleFiles(e.dataTransfer.files); }}
              className={`border-2 border-dashed border-brand-border rounded-lg p-6 text-center transition-colors ${uploading ? 'bg-brand-bg cursor-wait' : 'cursor-pointer hover:bg-brand-bg'}`}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-8 w-8 text-brand-indigo/60 mx-auto mb-2 animate-spin" />
                  <div className="text-sm font-semibold text-brand-indigo">Uploading… {progress}%</div>
                  <div className="h-1.5 mt-2 bg-brand-border rounded-full overflow-hidden max-w-xs mx-auto">
                    <div className="h-full bg-brand-green" style={{ width: `${progress}%` }} />
                  </div>
                </>
              ) : (
                <>
                  <UploadCloud className="h-8 w-8 text-brand-indigo/60 mx-auto mb-2" />
                  <div className="text-sm font-semibold text-brand-indigo">Upload signed report</div>
                  <div className="text-[11px] text-muted-foreground mt-1">PDF, PNG or JPG · up to 5 MB · stored on session record</div>
                </>
              )}
              <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No signed report on file yet.</div>
          )
        )}

        <div className="mt-3 text-[10px] text-muted-foreground leading-relaxed">
          Retain the client-signed report against this session for AMFI / SEBI inspection (minimum 5 years).
        </div>
      </CardContent>
    </Card>
  );
};

export default SignedReportUpload;
